import React, { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import Global from '../../../store/global-selectors';
import { groupByCarrierAndCustom, groupByCustomAndAddGenericPackage, CUSTOM_PACKAGE_SET_GROUP_ID } from './package-group-resolver';
import PackageDropdown from './index';
import { PackageService } from '../../../services/package-service';
import { useFulfillmentPlannerContext } from '../../context';
import { useGetPackageIds } from './package-id-helpers';
import { useEnablePackageDropdownGenericFallback } from '../../../feature-flags/hooks/use-enable-package-dropdown-generic-fallback';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';
import { useRateShopper } from '../../../pages/settings/rate-shopper/hooks/use-rate-shopper';

const getGroupingFunction = (sellerCarriers, selectedService) => {
  if (!selectedService || !selectedService.carrierId) {
    return groupByCustomAndAddGenericPackage;
  }

  const carrier = sellerCarriers.find((c) => c.carrierId === selectedService.carrierId);
  const carriers = carrier ? [carrier] : [];
  return (packages, isPackageDropdownGenericFallbackEnabled) =>
    groupByCarrierAndCustom(packages, carriers, isPackageDropdownGenericFallbackEnabled);
};

const getGroupedPackages = (state, serviceId, isPackageDropdownGenericFallbackEnabled) => {
  const selectedService = Global.selectSellerServiceById(state, { serviceId });
  const packages = Global.selectSellerPackages(state);
  const sellerCarriers = Global.selectReferenceDatagetSellerCarriers(state) || [];

  const filterFunction = PackageService.getFilterFunction(selectedService);
  const groupingFunction = getGroupingFunction(sellerCarriers, selectedService);
  const filteredPackages = packages.filter((pkg) => filterFunction(pkg));

  return groupingFunction(filteredPackages, isPackageDropdownGenericFallbackEnabled);
};

const getGroupedPackagesWithPackageSets = (state, serviceId, isPackageDropdownGenericFallbackEnabled) => {
  const selectedService = Global.selectSellerServiceById(state, { serviceId });
  const packages = Global.selectSellerPackages(state);
  const sellerCarriers = Global.selectReferenceDatagetSellerCarriers(state) || [];
  const packageSets = Global.selectSellerPackageSets(state) || [];

  const combinedPackages = [...packages, ...packageSets];

  const filterFunction = PackageService.getFilterFunction(selectedService);
  const groupingFunction = getGroupingFunction(sellerCarriers, selectedService);
  const filteredPackages = combinedPackages.filter((pkg) => filterFunction(pkg));

  return groupingFunction(filteredPackages, isPackageDropdownGenericFallbackEnabled);
};

/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {import('ui-components/components/dropdown-menu/dropdown-menu-type').DropdownMenuTypePropType} [props.dropdownMenuType]
 * @param {boolean} [props.isDisabled]
 * @param {() => void} [props.onTrackChangePackageSet]
 * @returns {JSX.Element}
 */
const PackageDropdownContainer = ({ id, dropdownMenuType, isDisabled = false, onTrackChangePackageSet = noop }) => {
  const {
    packages: contextPackages,
    onPackageChange,
    serviceIdAndSellerLabelProviderId,
    serviceCollectionGuid,
    applyPackageSetFromList,
  } = useFulfillmentPlannerContext();
  const { serviceId } = serviceIdAndSellerLabelProviderId;
  const packageIds = useGetPackageIds(contextPackages, serviceId);
  const { prePopulatePackage } = useRateShopper();
  const disablePackageDropdown = isDisabled || !!(prePopulatePackage && serviceCollectionGuid) || contextPackages.length > 1;
  const enableMultiPackageShipments = useMultiPackageShipments();
  const isPackageDropdownGenericFallbackEnabled = useEnablePackageDropdownGenericFallback();

  const packageGroups = useSelector((state) => {
    // TODO: only use this return when the feature flag is removed
    if (enableMultiPackageShipments) {
      return getGroupedPackagesWithPackageSets(state, serviceId, isPackageDropdownGenericFallbackEnabled);
    }

    return getGroupedPackages(state, serviceId, isPackageDropdownGenericFallbackEnabled);
  });

  const onChangePackageSet = useCallback(
    (packageSetId) => {
      const packageGroup = packageGroups.find((group) => group.id === CUSTOM_PACKAGE_SET_GROUP_ID);
      const packageSet = packageGroup?.packages?.find((pkg) => pkg.packageSetId === packageSetId);

      if (packageSet) {
        onTrackChangePackageSet(packageSet);
        applyPackageSetFromList(packageSet, CUSTOM_PACKAGE_SET_GROUP_ID);
      }
    },
    [applyPackageSetFromList, packageGroups, onTrackChangePackageSet],
  );

  return (
    <PackageDropdown
      disabled={disablePackageDropdown}
      dropdownMenuType={dropdownMenuType}
      id={id}
      onChange={onPackageChange}
      onChangePackageSet={onChangePackageSet}
      packageGroups={packageGroups}
      selectedPackageIds={packageIds}
      serviceCollectionGuid={serviceCollectionGuid}
    />
  );
};

export default PackageDropdownContainer;
